import { ComponentProps, useCallback, useRef, useState } from "react";
import { Modal, ModalContent } from "../../../ui/modal";
import { ModalData, ModalShowParams } from "../types";
import { notifyModalVisibility } from "../visibility";

type IModalProps = ComponentProps<typeof Modal>;
type IModalContentProps = ComponentProps<typeof ModalContent>;

export function useModal() {
  const [isVisible, setIsVisible] = useState(false);
  // Read synchronously by `ModalProvider.isVisible()`, so a `show` is seen
  // before React flushes the state update.
  const visibleRef = useRef(false);
  // Kept in a ref: a replacing `show()` must drop it without running it.
  const onHideRef = useRef<(() => void) | undefined>(undefined);
  const [data, setData] = useState<ModalData>({
    children: null,
  });

  const [options, setOptions] = useState<
    Omit<IModalProps, "onClose" | "isOpen" | "children">
  >({});
  const [enableSwipeToDismiss, setEnableSwipeToDismiss] = useState<
    boolean | undefined
  >(undefined);
  const [contentProps, setContentProps] = useState<
    Omit<IModalContentProps, "children">
  >({});

  const show = useCallback((params: ModalShowParams) => {
    const {
      children,
      contentProps: newContentProps,
      enableSwipeToDismiss: newEnableSwipeToDismiss,
      onHide,
      ...newOptions
    } = params;
    setData({
      children: children ?? null,
    });
    setOptions(newOptions);
    newContentProps && setContentProps(newContentProps);
    setEnableSwipeToDismiss(newEnableSwipeToDismiss);
    onHideRef.current = onHide;
    visibleRef.current = true;
    setIsVisible(true);
    notifyModalVisibility();
  }, []);

  const hide = useCallback(() => {
    const wasVisible = visibleRef.current;
    const onHide = onHideRef.current;
    onHideRef.current = undefined;
    visibleRef.current = false;
    setIsVisible(false);
    setData({
      children: null,
    });
    setOptions({});
    setContentProps({});
    setEnableSwipeToDismiss(false);
    if (!wasVisible) return;
    onHide?.();
    notifyModalVisibility();
  }, []);

  const getIsVisible = useCallback(() => visibleRef.current, []);

  return {
    isVisible,
    getIsVisible,
    show,
    hide,
    data,
    options,
    contentProps,
    enableSwipeToDismiss,
  };
}
